import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SubTitle,
  RightBar,
  Listening,
  RightItemBlock,
  RightSubTitle,
  TimeLog,
  TimerHead,
  TimeLogTitle,
  TimeLogContent,
  RightItemBlocks,
  Timer,
  Action,
  Time,
  Text,
  OverLayDiv,
  ProgressBar,
  ProgressWrapper,
  Timelog,
  TotalTrackedTime
} from './styles';
import AssignBoxV2 from '../../../../component/dropdowns/assignPopup';
import { useTaskAssignActions } from '../../../../component/dropdowns/assignPopup/assignActions';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import { isEmpty, floatToHHMMSS, getUTCDate, formatSecondsDuration, getRawComment } from '../../../../helpers/common';
import { COMMENT_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import { createNewComment, getTimelogData, updateTaskDetails } from '../../../../services/taskServices';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { TaskGroupInterface } from '../../../../interfaces/TaskInterface';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { nanoid } from 'nanoid';
import {
  addTimeLogDetails,
  getActiveTime,
  startTracker,
  stopTracker,
  updateTimeLogDetails
} from '../../../../services/timeTrackingServices';
import { captureException } from '../../../../services/logService';
import { TrackingActivityInterface } from '../../../../interfaces/TimeTrackingInterface';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import Tooltip from '../../../../component/tooltip';
import { rootStore } from '../../../../mobx/rootStore';
import { TaskModel } from '../../../../mobx/models/task';
import { RawTimeLogDataInterface } from '../../../../mobx/interfaces/task';
import PriorityDropdownV2 from '../../../../component/dropdowns/priorityDropdown';
import GroupDropdown from '../../../../component/dropdowns/statusDropdown';
import TaskStatusDropdownV2 from '../../../../component/dropdowns/taskStatusDropdown';
import EstimateDropdownV2 from '../../../../component/dropdowns/estimateDropdown';
import LabelDropdownMenuV2 from '../../../../component/dropdowns/labelDropdown';
import { observer } from 'mobx-react-lite';
import { computed } from 'mobx';
import CalendarPopUpV2 from '../../../../component/calendarPopUp';
import MilestoneDropdown from '../../../../component/dropdowns/milestoneDropdown';
import VercelCard from '../../../../component/vercelCard';
import IconButton from '../../../../component/button/iconButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

interface Props {
  onUpdateStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  setIsMilestonmodelopen?: (value: boolean) => void;
  setAlertModalOpen?: (value: boolean) => void;
  isTaskDetailPreview?: boolean;
  taskId: string;
}

interface TaskTimerProps {
  taskId: string;
  isRunning: boolean;
  timerStartLoading: boolean;
  onTimerClick: () => void;
}

// Excludes the current user's running log; callers add its live elapsed time from `currentStartTime`.
const getTrackedSeconds = (timeLogs: RawTimeLogDataInterface[] | undefined, userId: string | undefined) => {
  const totalHours =
    timeLogs?.reduce((sum, item) => sum + (item?.timerStartedAt && item.createdBy === userId ? 0 : item.hours), 0) || 0;
  return Math.max(0, totalHours * 3600);
};

// The navbar timer replaces `currentStartTime` every second, so only this component reads it.
const TaskTimer = observer(function TaskTimer({ taskId, isRunning, timerStartLoading, onTimerClick }: TaskTimerProps) {
  const { currentUser, taskStore, timeTrackingStore } = rootStore;
  const { currentTask: taskDetails } = taskStore;

  // Re-renders only when this task's elapsed time changes, not on every tick of another task's timer.
  const elapsedSecondsComputed = useMemo(
    () =>
      computed(() => {
        const { task_id, time } = timeTrackingStore.currentStartTime;
        return task_id === taskId ? Math.max(0, time || 0) : 0;
      }),
    [timeTrackingStore, taskId]
  );
  const elapsedSeconds = elapsedSecondsComputed.get();

  const trackedSeconds = getTrackedSeconds(taskDetails?.timeLogs, currentUser?.id);

  const estimateSeconds = taskDetails?.estimate && taskDetails.estimate > 0 ? taskDetails.estimate * 3600 : 0;
  const totalSeconds = isRunning ? trackedSeconds + elapsedSeconds : trackedSeconds;
  const isOverdue = estimateSeconds > 0 && trackedSeconds + elapsedSeconds > estimateSeconds;

  return (
    <Timer>
      <IconButton
        iconName={isRunning ? 'pause-icon' : 'play-icon'}
        variant={ICON_BUTTON_TYPE.SECONDARY}
        size={ICON_BUTTON_SIZE.MEDIUM}
        onClick={onTimerClick}
        type='rounded'
        className={'active-timer ' + (timerStartLoading ? 'pointer-event-none' : 'pointer-event-auto')}
      />
      <Action>
        <ProgressWrapper>
          <ProgressBar
            // PROPS: TOTAL TIME OVER THE ESTIMATION TIME
            isTimeOver={isOverdue}
            progress={
              estimateSeconds > 0 ? Math.min(((trackedSeconds + elapsedSeconds) / estimateSeconds) * 100, 100) : 0
            }></ProgressBar>
        </ProgressWrapper>
        <Tooltip
          placement='bottomLeft'
          maxWidth='max-content'
          title={
            <>
              <Timelog>
                Total time: <span>{formatSecondsDuration(totalSeconds, 'label')}</span>
              </Timelog>
              {estimateSeconds > 0 && (
                <>
                  <Timelog>
                    Estimated time: <span>{formatSecondsDuration(estimateSeconds, 'label')}</span>
                  </Timelog>
                  {isOverdue && (
                    <Timelog>
                      Exceed time:{' '}
                      <span>{formatSecondsDuration(trackedSeconds + elapsedSeconds - estimateSeconds, 'label')}</span>
                    </Timelog>
                  )}
                </>
              )}
            </>
          }>
          <Time isLargeHours={totalSeconds > 36000}>
            <span>{formatSecondsDuration(totalSeconds, 'colon')}</span>
          </Time>
        </Tooltip>
      </Action>
    </Timer>
  );
});

// Parent task total = own tracked time + all sub tasks tracked time (sub tasks total comes from the API).
const SubTasksTotalTime = observer(function SubTasksTotalTime({ taskId }: { taskId: string }) {
  const { currentUser, taskStore, timeTrackingStore } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  const subTasksTimeLog = taskDetails?.subTasksTimeLog;

  const elapsedComputed = useMemo(
    () =>
      computed(
        () => {
          const { task_id, time } = timeTrackingStore.currentStartTime;
          const elapsed = Math.max(0, time || 0);
          if (task_id === taskId) return { task: elapsed, subTasks: 0 };
          if (task_id && subTasksTimeLog?.subTaskIds.includes(task_id)) return { task: 0, subTasks: elapsed };
          return { task: 0, subTasks: 0 };
        },
        { equals: (a, b) => a.task === b.task && a.subTasks === b.subTasks }
      ),
    [timeTrackingStore, taskId, subTasksTimeLog]
  );
  const elapsed = elapsedComputed.get();

  if (!subTasksTimeLog?.subTaskIds.length) return null;

  const taskSeconds = getTrackedSeconds(taskDetails?.timeLogs, currentUser?.id) + elapsed.task;
  const subTasksSeconds = subTasksTimeLog.hours * 3600 + elapsed.subTasks;
  const totalSeconds = taskSeconds + subTasksSeconds;
  if (totalSeconds <= 0) return null;
  const subTaskCount = subTasksTimeLog.subTaskIds.length;

  return (
    <TotalTrackedTime>
      <TimeLogTitle>Total Tracked Time</TimeLogTitle>
      <Tooltip
        placement='bottomRight'
        maxWidth='max-content'
        title={
          <>
            <Timelog>
              This task: <span>{formatSecondsDuration(taskSeconds, 'label')}</span>
            </Timelog>
            <Timelog>
              Subtasks ({subTaskCount}): <span>{formatSecondsDuration(subTasksSeconds, 'label')}</span>
            </Timelog>
          </>
        }>
        <span>{floatToHHMMSS(totalSeconds / 3600)}</span>
      </Tooltip>
    </TotalTrackedTime>
  );
});

function RightBlock(props: Props) {
  const { onUpdateStatus, setAlertModalOpen, isTaskDetailPreview, taskId } = props || {};
  // States
  const [isEstimateDropdownOpen, setIsEstimateDropdownOpen] = useState(false);
  const [timerStartLoading, setTimerStartLoading] = useState(false);
  //Mobx state variables
  const { taskStore, projectStore, currentUser, workspaceStore, timeTrackingStore, settingStore } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  const { currentProject } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { activeTimeLogs } = timeTrackingStore;
  const isTimerRunning = activeTimeLogs?.some((x: { TaskId: string }) => x?.TaskId === taskId) ?? false;
  const customerRole = workspace?.customerRole;
  const assignActions = useTaskAssignActions(taskDetails ?? undefined);

  useEffect(() => {
    return () => {
      rootStore.taskStore.clearCurrentAssignBoxTaskId();
    };
  }, []);

  const loadedSubTaskCount = taskDetails?.subTasks?.length ?? 0;
  const isParentWithSubTasks = !taskDetails?.parentTaskId && loadedSubTaskCount > 0;
  // Task's own project: currentProject can still be the previous one while switching projects.
  const taskProjectId = taskDetails?.projectId;
  const activeTaskIdsKey = activeTimeLogs?.map((log) => log?.TaskId).join(',') ?? '';
  const prevActiveTaskIdsKey = useRef(activeTaskIdsKey);

  // Sub tasks total comes with the parent's time logs; load it when the task turns out to be a parent
  // or its sub task count changes. Reuses the page's initial request if it is still in flight.
  useEffect(() => {
    if (!isEmpty(customerRole)) return;
    const task = rootStore.taskStore.getTaskById(taskId);
    if (!isParentWithSubTasks) {
      task?.setSubTasksTimeLog(null);
      return;
    }
    if (task?.subTasksTimeLog?.subTaskIds.length === loadedSubTaskCount) return;
    getTimelogData(taskProjectId, taskId, true);
  }, [customerRole, isParentWithSubTasks, loadedSubTaskCount, taskProjectId, taskId]);

  // A timer started/stopped on one of the sub tasks changes the total; this task's own timer is refetched by its callers.
  useEffect(() => {
    const previous = prevActiveTaskIdsKey.current.split(',');
    const current = activeTaskIdsKey.split(',');
    prevActiveTaskIdsKey.current = activeTaskIdsKey;
    const subTaskIds = rootStore.taskStore.getTaskById(taskId)?.subTasksTimeLog?.subTaskIds;
    if (!subTaskIds?.length) return;
    const changed = [
      ...previous.filter((id) => !current.includes(id)),
      ...current.filter((id) => !previous.includes(id))
    ];
    if (changed.some((id) => subTaskIds.includes(id))) getTimelogData(taskProjectId, taskId);
  }, [activeTaskIdsKey, taskProjectId, taskId]);

  const onClickStartStop = useCallback(
    async (itemId: string, startingTime: string, item: TrackingActivityInterface) => {
      try {
        setTimerStartLoading(true);
        const date1 = new Date(startingTime).getTime();
        const date2 = new Date().getTime();
        const timeDifference = date2 - date1; // Difference in milliseconds
        const hoursDifference = timeDifference / (1000 * 60 * 60);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

        const updatedHistory = item?.History?.map((history) => {
          const { __typename, ...other } = history;
          return other;
        });

        const historyHoursSum = item?.History?.reduce((total, historyItem) => {
          return total + (historyItem?.Hours || 0);
        }, 0);

        const payloadUpdate = {
          Hours: Number(Number(historyHoursSum || 0) + hoursDifference),
          TimerStartedAtUnset: true,
          History: [
            ...(updatedHistory || []),
            {
              _id: nanoid(),
              CreatedBy: userDetails?.id,
              CreatedTime: new Date().toISOString(),
              UpdatedBy: userDetails?.id,
              UpdatedTime: new Date().toISOString(),
              TimerStartedAt: startingTime,
              TimerEndedAt: new Date().toISOString(),
              Hours: hoursDifference
            }
          ]
        };
        const result = await updateTimeLogDetails(itemId, payloadUpdate);

        if (result) {
          stopTracker();
          rootStore.alertNotificationStore.setSuccessMessage('Time Stopped!');
          await getTimelogData(currentProject?.id, taskId);
        }
        await getActiveTime();
      } catch (e) {
        captureException(e);
      } finally {
        setTimerStartLoading(false);
      }
    },
    [currentProject?.id, taskId]
  );

  const startTime = useCallback(async () => {
    setTimerStartLoading(true);
    try {
      if (activeTimeLogs?.length > 0) {
        if (setAlertModalOpen) setAlertModalOpen(true);
        return;
      }
      const payload = {
        project_id: currentProject?.id,
        task_id: taskId,
        date: getUTCDate(new Date()).toISOString(),
        hours: 0,
        startTime: true,
        trackingStartTime: new Date().toISOString()
      };
      const result = await addTimeLogDetails(payload);
      if (result) {
        await startTracker(taskId);
        // Run these two together (not one by one) so the timer refreshes faster.
        await Promise.all([getActiveTime(), getTimelogData(currentProject?.id, taskId)]);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setTimerStartLoading(false);
    }
  }, [activeTimeLogs, currentProject?.id, taskId, setAlertModalOpen]);

  const updateTaskGroup = useCallback(
    async (item: TaskGroupInterface) => {
      if (!currentUser) return;
      if (item?.id !== taskDetails?.groupId) {
        const previousGroup = taskDetails?.project?.taskGroups?.find((x) => x?.id === taskDetails?.groupId);
        const newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.CHANGE_GROUP,
          previousGroup: previousGroup?.name,
          group: item?.name
        };
        taskDetails?.setGroup(item?.id);
        const updatedComment = getRawComment(newComment, currentUser);
        taskDetails?.upsertComment(updatedComment);
        const payloadTask = { groupId: item?.id, projectId: taskDetails?.projectId };
        const result = await updateTaskDetails(taskDetails?.id, payloadTask);
        const commentResult = await createNewComment(taskDetails?.id, {
          Type: COMMENT_TYPE.CHANGE_GROUP,
          PreviousGroup: previousGroup?.name,
          Group: item?.name
        });

        if (result && commentResult) {
          trackAnalyticActivity(TASK_ANALYTICS.GROUP_CHANGED);
        }
      }
    },
    [currentUser, taskDetails]
  );

  const getGroupName = useCallback(() => {
    if (!taskDetails?.groupId) return;
    const groupName = taskDetails?.project?.taskGroups?.find((item) => item?.id === taskDetails?.groupId);
    return groupName?.id;
  }, [taskDetails]);

  // Handles timer start/stop functionality based on current tracking state
  const handleTimerClick = useCallback(() => {
    if (timerStartLoading) return; // Prevent click when loading
    if (isTimerRunning)
      onClickStartStop(activeTimeLogs[0]['_id'], activeTimeLogs[0]?.TimerStartedAt, activeTimeLogs[0]);
    else startTime();
  }, [timerStartLoading, isTimerRunning, activeTimeLogs, onClickStartStop, startTime]);

  const renderTaskTimeLog = useMemo(() => {
    if (!isEmpty(customerRole)) return null;
    return (
      <TimeLog>
        <TimerHead>
          <TimeLogTitle>Time Log</TimeLogTitle>
          {(taskDetails?.timeLogs && taskDetails?.timeLogs?.length > 0) || isTimerRunning ? (
            <TaskTimer
              taskId={taskId}
              isRunning={isTimerRunning}
              timerStartLoading={timerStartLoading}
              onTimerClick={handleTimerClick}
            />
          ) : (
            <IconButton
              iconName='play-icon'
              variant={ICON_BUTTON_TYPE.SECONDARY}
              size={ICON_BUTTON_SIZE.MEDIUM}
              onClick={startTime}
              type='rounded'
            />
          )}
        </TimerHead>
        {taskDetails?.timeLogs?.map((item) => {
          const dueTime = dayjs(item?.createdOn).fromNow();
          return (
            <TimeLogContent key={item?.id}>
              <SubTitle>
                {item?.userName} <span>{floatToHHMMSS(item?.hours)} logged.</span>
                <Tooltip
                  placement='topLeft'
                  maxWidth={'max-content'}
                  title={dayjs(item?.createdOn).format(`${currentUser?.dateFormat}, h:mm:ss a`)}>
                  <Text>{dueTime}</Text>
                </Tooltip>
                {item?.timerStartedAt && <>{' (Running)'}</>}
              </SubTitle>
            </TimeLogContent>
          );
        })}
      </TimeLog>
    );
  }, [
    currentUser?.dateFormat,
    customerRole,
    handleTimerClick,
    isTimerRunning,
    startTime,
    taskDetails?.timeLogs,
    taskId,
    timerStartLoading
  ]);

  const handleUpdateStatus = useCallback(
    (status: StatusListInterface, previousStatus: StatusListInterface) =>
      onUpdateStatus(taskDetails!, status, previousStatus),
    [onUpdateStatus, taskDetails]
  );

  return (
    <>
      <RightBar isTaskDetailPreview={isTaskDetailPreview}>
        <Listening hideBorderBottom={!isEmpty(customerRole)}>
          {currentProject?.statusEnable && currentProject?.statusData?.length > 0 && (
            <RightItemBlocks>
              <RightSubTitle className='left'>Status</RightSubTitle>
              <TaskStatusDropdownV2
                defaultStatus={currentProject?.defaultStatus}
                statusList={currentProject?.statusData}
                currentStatusId={taskDetails?.statusId}
                onUpdateStatus={handleUpdateStatus}
              />
            </RightItemBlocks>
          )}
          {currentProject?.isGroupEnabled &&
            taskDetails?.project?.taskGroups &&
            taskDetails?.project?.taskGroups?.length > 0 && (
              <RightItemBlocks>
                <RightSubTitle className='left'>Group</RightSubTitle>
                <GroupDropdown
                  onUpdateGroup={(group) => updateTaskGroup(group)}
                  currentProject={taskDetails?.project}
                  value={getGroupName()}
                />
              </RightItemBlocks>
            )}
          {currentProject?.priority?.enabled === true && (
            <RightItemBlocks>
              <RightSubTitle className='left'>Priority</RightSubTitle>
              <PriorityDropdownV2 taskData={taskDetails!} currentProject={currentProject!} />
            </RightItemBlocks>
          )}
          {currentProject?.isMilestone &&
            taskDetails?.project?.milestones &&
            taskDetails?.project?.milestones?.length > 0 && (
              <RightItemBlocks>
                <RightSubTitle className='left'>Milestone</RightSubTitle>
                <MilestoneDropdown />
              </RightItemBlocks>
            )}
          {currentProject?.labelsEnabled && currentProject?.labelsList?.length > 0 && (
            <RightItemBlocks className={currentProject?.labelsEnabled && 'label'}>
              <RightSubTitle className='left'>Label</RightSubTitle>
              <LabelDropdownMenuV2 taskData={taskDetails!} currentProject={currentProject!} isFromTaskDetails={true} />
            </RightItemBlocks>
          )}
          {currentProject?.isEstimate && (
            <RightItemBlocks>
              <RightSubTitle className='left'>Estimate</RightSubTitle>
              <EstimateDropdownV2 isTaskDetails={true} taskData={taskDetails!} />
            </RightItemBlocks>
          )}
          <RightItemBlocks>
            <RightSubTitle className='left'>Due Date</RightSubTitle>
            <CalendarPopUpV2 data={taskDetails || undefined} />
          </RightItemBlocks>
          <RightItemBlock isShowHover={true}>
            <RightSubTitle className='left'>Assign</RightSubTitle>
            <AssignBoxV2
              projectUsers={currentProject?.users || []}
              context={{ mode: 'taskDetail', currentTask: taskDetails! }}
              onAssign={assignActions.onAssign}
              onUnassign={assignActions.onUnassign}
              isMultiAssignee={currentProject?.multiAssignee}
            />
          </RightItemBlock>
        </Listening>
        {/* If vercel is enable in current project */}
        {settingStore.settingIntegration?.vercel_connected && currentProject?.vercelProject?.id && <VercelCard />}
        {isEmpty(customerRole) && <SubTasksTotalTime taskId={taskId} />}
        {renderTaskTimeLog}
      </RightBar>
      {isEstimateDropdownOpen && (
        <OverLayDiv role='button' className='dropdown-overlay' onClick={() => setIsEstimateDropdownOpen(false)} />
      )}
    </>
  );
}

export default observer(RightBlock);
